"use client";
import { AppError } from "@/shared/AppError";
import { parseBackendError } from "@/utils/apiUtils";
import {
  sendAuthenticatedRequestToApi,
  sendRequestToApi,
} from "@/utils/axiosUtils";
import CONSTS from "@/utils/consts";
import { reportError } from "@/utils/reportErrorUtils";
import { supabase } from "@/utils/supabaseUtils";
import * as Sentry from "@sentry/nextjs";
import { Session } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface RegisterParams {
  activationCode: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

type AuthContextType = {
  isAuthenticated: boolean | undefined;
  login: (email: string, password: string) => Promise<void>;
  registerWithGoogle: (activationCode: string) => Promise<void>;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  signInWithGoogle: () => Promise<void>;
  authProviders: string[];
  logout: () => Promise<void>;
  register: (params: RegisterParams) => Promise<void>;
  isLoading: boolean;
  userEmail: string | null;
  getUserAuthEmail: () => string | null;
  getUserProviders: () => string[];
  isAdmin: boolean | undefined;
  impersonatedCaseId: string | null;
  setImpersonation: (caseId: string) => Promise<void>;
  clearImpersonation: () => void;
  supabaseToken: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Thrown when the backend rejects a token that the Supabase client still
// considers valid (e.g. the auth user was deleted/re-created server-side).
// Carries the original request error as `cause` so Sentry's beforeSend
// (addAxiosContextRecursive) can attach URL, status, and response payload.
class StaleSessionError extends Error {
  constructor(cause: unknown) {
    super("Backend rejected the Supabase token; local session is stale.");
    this.name = "StaleSessionError";
    this.cause = cause;
  }
}

type AuthContextProviderProps = {
  children: React.ReactNode;
};

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [authProviders, setAuthProviders] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(undefined);
  const [impersonatedCaseId, setImpersonatedCaseId] = useState<string | null>(
    null
  );
  const [supabaseToken, setSupabaseToken] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  // Incremented to invalidate an in-flight processSession run (each new run
  // claims a generation; the watchdog bumps it so late completions are ignored).
  const processingGenerationRef = useRef(0);

  const resetAuthData = useCallback(() => {
    setIsAuthenticated(false);
    setIsLoading(false);
    setIsAdmin(undefined);
    setAuthProviders([]);
    setUserEmail(null);
    setSupabaseToken(null);
    setImpersonatedCaseId(null);
    localStorage.removeItem("impersonatedCaseId");
    Sentry.setUser(null);
  }, []);

  const checkAdminStatus = useCallback(async (token: string | null) => {
    if (!token) {
      setIsAdmin(false);
      return false;
    }
    try {
      const response = await sendAuthenticatedRequestToApi({
        route: "login-area/auth/check-admin",
        method: "GET",
        token,
      });
      const isAdminResponse = Boolean(response?.data?.data?.isAdmin);
      setIsAdmin(isAdminResponse);
      return isAdminResponse;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        throw new StaleSessionError(error);
      }
      reportError(error);
      setIsAdmin(false);
      return false;
    }
  }, []);

  // We keep the useEffect wrt the supabase onAuthStateChange function as minimal as possible.
  // Before we did a lot in the callback and eventually had to make it an async function.
  // This caused a deadlock, when opening multiple tabs but even in one tab it lead at times to
  // crashing of the whole app by never updating the `isAuthenticated` property.
  // Whatever you do, do not touch this callback function. We only want to set the session and when
  // that is done, we handle everything else in the useEffect below this one.
  // Docs for this: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
  // Important: A callback can be an async function and it runs synchronously during the processing of the
  //  changes causing the event. You can easily create a dead-lock by using await on a call to another method of the Supabase library.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // If Supabase never settles the initial session (e.g. refresh_token requests
  // hang/fail without emitting SIGNED_OUT), isLoading would stay true forever.
  // Only runs while session is still undefined — cleared as soon as auth settles.
  useEffect(() => {
    if (session !== undefined) return;

    const timeoutId = window.setTimeout(() => {
      reportError(
        new Error(
          "Auth session init timed out; treating user as logged out and clearing local session."
        )
      );
      setSession(null);
      // Fire-and-forget: UI state is already reset above; this only clears the
      // persisted session. Surface failures instead of discarding them.
      supabase.auth
        .signOut({ scope: "local" })
        .then(({ error }) => error && reportError(error))
        .catch(reportError);
    }, 4_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [session]);

  // Covers hangs after the session was delivered, e.g. the check-admin
  // request never settling. Any auth-loading state that outlives this
  // timeout resolves to logged out rather than an infinite loading screen.
  useEffect(() => {
    // Session init has its own watchdog above; the 10s budget here should
    // only start once the session has been delivered.
    if (session === undefined) return;
    if (!isLoading) return;

    const timeoutId = window.setTimeout(() => {
      // Invalidate the in-flight processSession synchronously, so a late
      // checkAdminStatus completion can't re-authenticate the user after
      // this cleanup (signOut's SIGNED_OUT event arrives asynchronously).
      processingGenerationRef.current++;
      reportError(
        new Error(
          "Auth processing timed out; treating user as logged out and clearing local session."
        )
      );
      resetAuthData();
      supabase.auth
        .signOut({ scope: "local" })
        .then(({ error }) => error && reportError(error))
        .catch(reportError);
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [session, isLoading, resetAuthData]);

  useEffect(() => {
    if (session === undefined) return;

    let mounted = true;
    const generation = ++processingGenerationRef.current;
    const isCancelled = () =>
      !mounted || generation !== processingGenerationRef.current;

    const processSession = async () => {
      try {
        if (!session?.user) {
          if (!isCancelled()) resetAuthData();
          return;
        }

        const providers = session.user.app_metadata?.providers;
        const token = session.access_token;
        const isAdminResponse = await checkAdminStatus(token);

        if (isCancelled()) return;

        if (localStorage.getItem("impersonatedCaseId") && isAdminResponse) {
          setImpersonatedCaseId(localStorage.getItem("impersonatedCaseId"));
        }
        setIsAdmin(isAdminResponse);
        setAuthProviders(providers || []);
        setUserEmail(session.user.email || null);
        setSupabaseToken(token || null);

        Sentry.setUser({
          id: session.user.id,
        });

        setIsAuthenticated(true);
        setIsLoading(false);
      } catch (error) {
        reportError(error);
        if (error instanceof StaleSessionError) {
          // Drop the session completely so the user ends up logged out
          // instead of stuck loading or ghost-authenticated without a case.
          void supabase.auth.signOut({ scope: "local" });
        }
        if (!isCancelled()) resetAuthData();
      }
    };

    processSession();

    return () => {
      mounted = false;
    };
  }, [session, checkAdminStatus, resetAuthData]);

  const register = async ({
    activationCode,
    email,
    password,
    passwordConfirmation,
  }: RegisterParams) => {
    const payload = {
      email,
      password,
      passwordConfirmation,
      activationCode,
    };
    try {
      await sendRequestToApi("login-area/auth/register", "POST", payload);
    } catch (err: any) {
      const appError = parseBackendError(err);
      throw appError;
    }
    await login(email, password);
  };

  const login = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new AppError({
          code: "AUTH_FAILED",
          internalMessage: `Supabase login error: ${error.message}`,
          uiMessage: "Ungültige Email oder Passwort.",
          isExpected: true,
        });
      }

      setIsAuthenticated(true);
    } catch (err: any) {
      if (err instanceof AppError) {
        throw err;
      } else {
        throw new AppError({
          code: "UNKNOWN_ERROR",
          internalMessage: `Unexpected login failure: ${
            err.message || String(err)
          }`,
          uiMessage:
            "Login ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.",
          isExpected: false,
        });
      }
    }
  };

  const registerWithGoogle = async (activationCode: string) => {
    try {
      const redirectUrl = `${CONSTS.FRONTEND_URL}/callback/register-with-google?activationCode=${activationCode}`;
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });
    } catch (err: any) {
      throw new AppError({
        code: "AUTH_FAILED",
        internalMessage: `Supabase Google OAuth error: ${
          err.message || String(err)
        }`,
        uiMessage:
          "Google-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
        isExpected: true,
      });
    }
  };

  const signInWithGoogle = async () => {
    try {
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${CONSTS.FRONTEND_URL}/`,
        },
      });
    } catch (err: any) {
      throw new AppError({
        code: "AUTH_FAILED",
        internalMessage: `Supabase Google OAuth sign-in error: ${
          err.message || String(err)
        }`,
        uiMessage:
          "Google-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
        isExpected: true,
      });
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem("impersonatedCaseId");
      await supabase.auth.signOut();
      setImpersonatedCaseId(null);
    } catch (err: any) {
      throw new AppError({
        code: "UNKNOWN_ERROR",
        internalMessage: `Supabase signOut error: ${
          err.message || String(err)
        }`,
        uiMessage: "Logout fehlgeschlagen. Bitte versuchen Sie es erneut.",
        isExpected: false,
      });
    }
  };

  const setImpersonation = async (caseId: string) => {
    try {
      const response = await sendAuthenticatedRequestToApi({
        route: "login-area/admin/impersonate-case",
        method: "POST",
        body: { caseId },
        token: supabaseToken,
      });

      if (response.status === 200) {
        setImpersonatedCaseId(caseId);
        localStorage.setItem("impersonatedCaseId", caseId);
      } else {
        setImpersonatedCaseId(null);
        localStorage.removeItem("impersonatedCaseId");
      }
    } catch (err: any) {
      const appError = parseBackendError(err);
      throw appError;
    }
  };

  const clearImpersonation = () => {
    setImpersonatedCaseId(null);
    localStorage.removeItem("impersonatedCaseId");
  };

  // Utility functions to get user data
  const getUserAuthEmail = () => userEmail;
  const getUserProviders = () => authProviders;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        authProviders,
        login,
        registerWithGoogle,
        signInWithGoogle,
        setIsAuthenticated,
        logout,
        register,
        isLoading,
        userEmail,
        getUserAuthEmail,
        getUserProviders,
        isAdmin,
        impersonatedCaseId,
        setImpersonation,
        clearImpersonation,
        supabaseToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("Context must be defined to use AuthContext Provider");
  }
  return context;
};
